import { scaleScore } from '../scaling';
import { wcpmLookupTable } from '../../values/scales';
import { locales } from '../../../../constants/constants';
import getArmScore from '../getArmScore';

export function generateScore(activity, metricId, scaleId, shouldRound = true, selectedLocale) {  
  // Define valid metric IDs that are supported by this function
  const validMetricIds = [
    'ORF', 'Lexile', 'SightRecognition', 'PhonologicalAwareness', 'VocabSize', 
    'ReadingMastery', 'ARM', 'ISIP', 'bkScore', 'compScore', 'Decoding', 
    'ISIP_overall_ISIPAR', 'ISIP_overall_universal', 'armScoreAdjusted', 'decodeScore', 'equatedWCPM', 
    'esriScore', 'lkScore', 'phonAwareScore', 'ranSpeed', 'spellingScore', 
    'vocabularyScreenerScore', 'ISIP_overall_ISIPAR_PR', 'armScorePR', 
    'bkScore_PR', 'compScore_PR', 'decodeScore_PR', 'equatedWCPMPR', 
    'ISIP_overall_universal_PR', 'vocabularyScreenerScore_PR', 'phonAwareScore_PR', 'esriScore_PR'
  ];
  
  // Check if metricId is valid before proceeding with validation
  const isValidMetric = validMetricIds.includes(metricId);
  
  if(!activity || !activity.scores || (!isValidMetric && !activity.scores.wcpmScore)) {
    return null;
  }

  let orfScore = null;
  // Handle ORF metric based on scaleId
  if(metricId === 'ORF') {
    if (scaleId === 'WCPM') {
      // Use raw wcpmScore for unadjusted scores
      orfScore = activity.scores.wcpmScore;
    } else if (scaleId === 'equatedWCPM') {
      // Use equatedWCPM for adjusted scores
      orfScore = activity.scores.equatedWCPM || activity.scores.wcpmScore;
    } else if (scaleId === 'Lexile') {
      orfScore = activity.scores.lexile;
    } else if (scaleId === 'DRA') {
      orfScore = activity.scores.dra;
    } else {
      // Fallback to scaleScore function for other scale types
      orfScore = scaleScore(activity.scores.wcpmScore, scaleId, activity);
    }
    
    // ORF score is clamped, such that it can't be greater than highest value in lookup table
    // If value is higher than this (currently 300 wcpm), it's likely a bug
    if(orfScore && orfScore > (wcpmLookupTable.length - 1)) {
      console.log('WARNING: unrealistically high ORF: ', orfScore);
      orfScore = wcpmLookupTable.length - 1;
    }
  }

  // TODO, to add in nwf and spelling:
  /*
  'NWF': activity.scores.nwf,
  'Spelling': activity.scores.spelling,
  */

  // search for "ARM SPANISH SCORES" to find other places where ARM score is determined.
  let arm = getArmScore(activity.student.id, activity, selectedLocale);
  if(activity.type !== 'assessment') {
    arm = activity.scores.currentArmEstimate;
  }

  let scores = {
    'ORF': formatScoreByType(orfScore, 'ORF'),
    'Lexile': formatScoreByType(activity.scores.lexile, 'Lexile'),
    'SightRecognition': activity.scores.esriScore,
    'PhonologicalAwareness': formatScoreByType(activity.scores.phonAwareScore, 'PhonologicalAwareness', shouldRound),
    'VocabSize': formatScoreByType(activity.scores.vocabSize, 'VocabSize', shouldRound),
    'ReadingMastery': formatScoreByType(activity.scores.areaScore, 'ReadingMastery'),
    'ARM': formatScoreByType(arm, 'ARM'),
    'ISIP': formatScoreByType(activity.scores.ISIP_overall_universal, 'ISIP'),
    'bkScore': formatScoreByType(activity.scores.bkScore, 'bkScore'),
    'compScore': formatScoreByType(activity.scores.compScore, 'compScore'),
    'Decoding': formatScoreByType(activity.scores.decodeScore, 'Decoding'),
    'ISIP_overall_ISIPAR': formatScoreByType(activity.scores.ISIP_overall_universal, 'ISIP_overall_ISIPAR'),
    'ISIP_overall_universal': formatScoreByType(activity.scores.ISIP_overall_universal, 'ISIP_overall_universal'),
    'armScoreAdjusted': formatScoreByType(activity.scores.armScoreAdjusted, 'armScoreAdjusted'),
    'decodeScore': formatScoreByType(activity.scores.decodeScore, 'decodeScore'),
    'equatedWCPM': formatScoreByType(activity.scores.equatedWCPM, 'equatedWCPM'),
    'esriScore': formatScoreByType(activity.scores.esriScore, 'esriScore'),
    'esriScore_PR': formatScoreByType(activity.scores.esriScore_PR, 'esriScore_PR'),
    'lkScore': formatScoreByType(activity.scores.lkScore, 'lkScore'),
    'phonAwareScore': formatScoreByType(activity.scores.phonAwareScore, 'phonAwareScore'),
    'phonAwareScore_PR': formatScoreByType(activity.scores.phonAwareScore_PR, 'phonAwareScore_PR'),
    'ranSpeed': formatScoreByType(activity.scores.ranSpeed, 'ranSpeed'),
    'spellingScore': formatScoreByType(activity.scores.spellingScore, 'spellingScore'),
    'vocabularyScreenerScore': formatScoreByType(activity.scores.vocabularyScreenerScore, 'vocabularyScreenerScore'),
    'vocabularyScreenerScore_PR': formatScoreByType(activity.scores.vocabularyScreenerScore_PR, 'vocabularyScreenerScore'),
    'ISIP_overall_ISIPAR_PR': formatScoreByType(activity.scores.ISIP_overall_universal_PR, 'ISIP_overall_ISIPAR_PR'),
    'armScorePR': formatScoreByType(activity.scores.armScorePR, 'armScorePR'),
    'bkScore_PR': formatScoreByType(activity.scores.bkScore_PR, 'bkScore_PR'),
    'compScore_PR': formatScoreByType(activity.scores.compScore_PR, 'compScore_PR'),
    'decodeScore_PR': formatScoreByType(activity.scores.decodeScore_PR, 'decodeScore_PR'),
    'equatedWCPMPR': formatScoreByType(activity.scores.equatedWCPMPR, 'equatedWCPM_PR'),
    'ISIP_overall_universal_PR': formatScoreByType(activity.scores.ISIP_overall_universal_PR, 'ISIP_overall_universal_PR'),
  }

  if(scores[metricId] || scores[metricId] == 0) {
    return scores[metricId];
  }

  if(!Object.keys(scores).includes(metricId)){
    console.log('error: invalid metricId: ', metricId)
  }

  return null;
}

/**
 * Formats a score based on its type
 * @param {number} score - The score to format
 * @param {string} type - The type of score (e.g., 'PhonologicalAwareness', 'VocabSize', 'ReadingMastery', 'ARM')
 * @param {boolean} shouldRound - Whether to round the score
 * @returns {number} The formatted score
 */
export const formatScoreByType = (score, type, shouldRound = true) => {
  if (score === null || score === undefined) {
    return null;
  }
  switch (type) {
    case 'ORF':
    case 'equatedWCPM':
    case 'ISIP':
    case 'ISIP_overall_ISIPAR':
    case 'ISIP_overall_universal':
    case 'Lexile':
      return Math.round(score);
    case 'VocabSize':
    case 'armScoreAdjusted':
    case 'esriScore':
    case 'lkScore':
    case 'spellingScore':
    case 'vocabularyScreenerScore':
    case 'PhonologicalAwareness':
    case 'phonAwareScore':
    case 'ARM':
    case 'bkScore':
    case 'compScore':
    case 'Decoding':
    case 'decodeScore':
    case 'ranSpeed':
      return parseFloat(parseFloat(score).toFixed(2));
    case 'Percentile':
      return Math.floor(score);
    default:
      return score;
  }
};

export default {
  generateScore,
}